// The board as the screens see it: sections per tab in a stable order, the selected card per
// tab, and pass progress. Order and selection live in module memory so they survive navigation
// between the board, a card's page and focus mode.
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import type { BoardView, CardSummary } from "@shared/api";
import { useActiveFeed, useBoard } from "@web/lib/feedQueries";
import {
  boardSections,
  passProgress,
  reselect,
  stableOrder,
  type BoardSections,
  type BoardTab,
} from "./selectors";

const orders = new Map<string, string[]>();
const selections = new Map<string, { id: string | null; index: number }>();
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version++;
  for (const l of listeners) l();
}

function useStoreVersion(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => version,
    () => version,
  );
}

export function selectionKey(feedId: string, tab: BoardTab): string {
  return `${feedId}:${tab}`;
}

export function getSelection(key: string): { id: string | null; index: number } {
  return selections.get(key) ?? { id: null, index: 0 };
}

export function setSelection(key: string, id: string | null, index: number): void {
  const current = selections.get(key);
  if (current?.id === id && current.index === index) return;
  selections.set(key, { id, index });
  emit();
}

/** Apply the remembered order for `key` to `cards` and remember the result. */
function ordered(key: string, cards: CardSummary[]): CardSummary[] {
  const ids = stableOrder(
    orders.get(key) ?? null,
    cards.map((c) => c.id),
  );
  orders.set(key, ids);
  const byId = new Map(cards.map((c) => [c.id, c]));
  return ids.map((id) => byId.get(id)!).filter(Boolean);
}

// Pass progress: every card id seen in a pass this browser session.
function seenKey(feedId: string, pass: number) {
  return `mm-pass-seen:${feedId}:${pass}`;
}

function readSeen(feedId: string, pass: number): string[] {
  try {
    const raw = sessionStorage.getItem(seenKey(feedId, pass));
    const value: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeSeen(feedId: string, pass: number, ids: string[]) {
  try {
    sessionStorage.setItem(seenKey(feedId, pass), JSON.stringify(ids));
  } catch {
    // storage unavailable: progress simply restarts on reload
  }
}

export interface BoardModel {
  feedId: string | null;
  board: BoardView | undefined;
  sections: BoardSections | null;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  noFeed: boolean;
  progress: { decided: number; total: number };
}

export function useBoardModel(): BoardModel {
  const { feedId, query: feeds } = useActiveFeed();
  const board = useBoard(feedId);
  const data = board.data;

  const sections = useMemo(() => {
    if (!data || !feedId) return null;
    const raw = boardSections(data);
    const pass = data.pass.current;
    return {
      ...raw,
      review: ordered(`${feedId}:${pass}:review`, raw.review),
      queued: ordered(`${feedId}:queued`, raw.queued),
      working: ordered(`${feedId}:working`, raw.working),
    };
  }, [data, feedId]);

  const reviewIds = sections?.review.map((c) => c.id).join(",") ?? "";
  const pass = data?.pass.current ?? 0;
  const progress = useMemo(() => {
    if (!feedId || !sections) return { decided: 0, total: 0 };
    const seen = readSeen(feedId, pass);
    const ids = sections.review.map((c) => c.id);
    const merged = [...new Set([...seen, ...ids])];
    if (merged.length !== seen.length) writeSeen(feedId, pass, merged);
    return passProgress(merged, ids);
    // reviewIds captures the membership that matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId, pass, reviewIds]);

  return {
    feedId,
    board: data,
    sections,
    isPending: feeds.isPending || (!!feedId && board.isPending),
    isError: feeds.isError || board.isError,
    refetch: () => {
      void feeds.refetch();
      void board.refetch();
    },
    noFeed: feeds.isSuccess && !feedId,
    progress,
  };
}

/**
 * Selected item in `ids` (routine groups, then cards) for this feed/tab, following it to a
 * neighbour when it leaves. With nothing remembered yet, selection starts at `startIndex` (the
 * first card, so a cleanup above the cards is one k away rather than in the way).
 */
export function useSelectedCard(
  feedId: string | null,
  tab: BoardTab,
  ids: readonly string[],
  startIndex = 0,
) {
  useStoreVersion();
  const key = feedId ? selectionKey(feedId, tab) : "";
  const remembered = key ? selections.get(key) : undefined;
  const current = remembered ?? { id: null, index: startIndex };
  const id = reselect(ids, current.id, current.index);
  const index = id ? ids.indexOf(id) : -1;

  useEffect(() => {
    if (key && (id !== current.id || (index >= 0 && index !== current.index))) {
      setSelection(key, id, Math.max(index, 0));
    }
  }, [key, id, index, current.id, current.index]);

  const select = useCallback(
    (nextId: string) => {
      if (!key) return;
      setSelection(key, nextId, Math.max(ids.indexOf(nextId), 0));
    },
    [key, ids],
  );

  const move = useCallback(
    (delta: number): string | null => {
      if (!key || !ids.length) return null;
      const from = index < 0 ? (delta > 0 ? -1 : ids.length) : index;
      const nextIndex = Math.min(ids.length - 1, Math.max(0, from + delta));
      const next = ids[nextIndex];
      if (!next) return null;
      setSelection(key, next, nextIndex);
      return next;
    },
    [key, ids, index],
  );

  return { selectedId: id, index, select, move, atEnd: index === ids.length - 1 };
}
